import { useEffect, useLayoutEffect } from 'react';

import env from '@travel/env';

import { logout } from 'store/authCode/actions';
import { getError, getIsLoggedIn } from 'store/authCode/selectors';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getLocation } from 'store/__router/selectors';
import { LOGIN_BASE_URL, LOGIN_RETURN_URL, LOGIN_SERVICE_ID } from 'constants/login';

type URLtype = {
  pathname: RegExp;
  search?: RegExp;
};
const AUTH_REQUIRED_URLS: Array<URLtype> = [
  { pathname: /^\/entry\/[post|edit]/, search: /.*/ },
  {
    pathname: /^\/reviewer\/main(\/|$)/,
  },
  { pathname: /^\/reviewer\/edit(\/|$)/ },
];

function isAuthRequiredUrl({ pathname, search }: { pathname: string; search?: string }): boolean {
  return AUTH_REQUIRED_URLS.some((pattern) => {
    return pattern.pathname.test(pathname) && (pattern.search?.test(search || '') ?? true);
  });
}

const useHeaderEvents = () => {
  const dispatch = useAppDispatch();
  const location = useAppSelector(getLocation);
  // if local env (mock-api), we will not trigger OMNI
  const isDevelopmentEnv = env('UNIVERSAL_API_MOCK_SERVER')?.toLowerCase() === 'true';
  const isLoggedIn = useAppSelector(getIsLoggedIn);
  const error = useAppSelector(getError);
  const isMaintenanceMode = error?.status === 503;
  const isExceptionError = error?.status === 404;

  useLayoutEffect(() => {
    // to trigger login if there is no token and accessing auth required pages
    // mock-env: skip forced user login on any error condition
    // non-mock-env: whenever there is no token or auth api is error, force user to login

    const submitLogin = () => {
      const form = document?.createElement('form');

      form.method = 'POST';
      form.action = LOGIN_BASE_URL;
      form.style.display = 'none';

      const data = {
        return_url: LOGIN_RETURN_URL,
        service_id: LOGIN_SERVICE_ID,
        f_next_url: window.location.href,
      };

      Object.entries(data).forEach(([key, value]) => {
        const input = document?.createElement('input');

        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document?.body.appendChild(form);
      form.submit();
    };

    const triggerLogin = () => {
      if (
        !isMaintenanceMode &&
        !isDevelopmentEnv &&
        !isExceptionError &&
        isAuthRequiredUrl(location) &&
        (!isLoggedIn || Boolean(error?.status))
      ) {
        submitLogin();
      }
    };

    window?.addEventListener('load', triggerLogin);

    return () => {
      window?.removeEventListener('load', triggerLogin);
    };
  }, [
    error?.status,
    isDevelopmentEnv,
    isMaintenanceMode,
    isExceptionError,
    isLoggedIn,
    location,
    dispatch,
  ]);

  // to register logout event
  useEffect(() => {
    function triggerLogout() {
      dispatch(logout());
    }

    document.addEventListener('HEADER_EVENT_LOGOUT', triggerLogout, false);

    return () => {
      document.removeEventListener('HEADER_EVENT_LOGOUT', triggerLogout, false);
    };
  }, [dispatch]);

  // to replace url to href path as the redirect url will have "?" (current platform rule)
  // only case "<url>?" after OMNI login will be replaced
  useEffect(() => {
    const pathRegex = new RegExp('\\?$');

    if (pathRegex.test(window.location.href)) {
      const href = window.location.href;

      window.history.replaceState({}, '', href.substring(0, href.length - 1));
    }
  });
};

export default useHeaderEvents;
